import { useState } from 'react';
import { useCart } from '../../../contexts/CartContext';

const PricingSection = () => {
  const { addItem, isInCart } = useCart();
  const [addedStates, setAddedStates] = useState<Record<string, boolean>>({});

  const plans = [
    {
      id: 'basic-plan',
      name: 'Basic',
      price: 100,
      priceDisplay: '$100',
      period: '',
      description: 'Essential IT support for small businesses just getting started',
      features: [
        'IT Helpdesk & Support',
        'Email & Phone Support',
        'Remote Troubleshooting',
        'Basic Security Setup',
        'Up to 10 Users',
        'Business Hours Support'
      ],
      highlighted: false,
      priceRange: '$100 - $200',
      icon: 'ri-customer-service-2-line',
      color: 'from-violet-500 to-purple-600'
    },
    {
      id: 'growth-plan',
      name: 'Growth',
      price: 400,
      priceDisplay: '$400',
      period: '',
      description: 'Comprehensive IT management for growing companies',
      features: [
        'Everything in Basic',
        'Network Setup & Management',
        'Cloud Hosting & Migration',
        'Cybersecurity Audit & Protection',
        'Software Licensing Management',
        'Up to 50 Users',
        'Priority Support',
        'Monthly Security Reports'
      ],
      highlighted: true,
      priceRange: '$250 - $400',
      icon: 'ri-rocket-line',
      color: 'from-purple-500 to-fuchsia-600'
    },
    {
      id: 'pro-plan',
      name: 'Pro',
      price: 750,
      priceDisplay: '$750',
      period: '',
      description: 'Enterprise-grade IT solutions with 24/7 monitoring',
      features: [
        'Everything in Growth',
        'Remote Monitoring & Management',
        'IT Training & Onboarding',
        '24/7 System Monitoring',
        'Automated Patch Management',
        'Unlimited Users',
        'Dedicated Account Manager',
        'Performance Analytics',
        'Predictive Maintenance',
        'Custom SLA Agreements'
      ],
      highlighted: false,
      priceRange: '$500 - $750',
      icon: 'ri-vip-crown-line',
      color: 'from-fuchsia-500 to-violet-600'
    }
  ];

  const handleBuyPlan = (plan: typeof plans[0]) => {
    addItem({
      id: plan.id,
      name: `${plan.name} Plan`,
      price: plan.price,
      icon: plan.icon,
      color: plan.color
    });

    setAddedStates(prev => ({ ...prev, [plan.id]: true }));
    
    setTimeout(() => {
      setAddedStates(prev => ({ ...prev, [plan.id]: false }));
    }, 2000);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-violet-100 text-violet-700 text-sm font-semibold rounded-full mb-4">
            Pricing Plans
          </div>
          <h2 className="text-5xl font-bold text-slate-900 mb-4">Transparent Pricing for Every Stage</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Choose the plan that matches your needs. Mix and match individual services or save with our bundled packages.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {plans.map((plan, index) => {
            const inCart = isInCart(plan.id);
            const justAdded = addedStates[plan.id];
            
            return (
              <div 
                key={index}
                className={`relative bg-white rounded-2xl p-8 ${
                  plan.highlighted 
                    ? 'border-2 border-violet-500 shadow-2xl scale-105 lg:scale-110' 
                    : 'border border-gray-200 shadow-md'
                } transition-all duration-300 hover:shadow-xl`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-violet-500 to-purple-600 text-white text-xs font-bold px-4 py-2 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <p className="text-sm text-gray-600 mb-6">{plan.description}</p>
                  <div className="flex items-end justify-center mb-2">
                    <span className="text-5xl font-bold text-slate-900">{plan.priceDisplay}</span>
                  </div>
                  <p className="text-xs text-violet-600 font-semibold">{plan.priceRange}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleBuyPlan(plan)}
                  disabled={inCart}
                  className={`block w-full text-center px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    inCart
                      ? 'bg-green-500 text-white cursor-not-allowed'
                      : justAdded
                      ? 'bg-green-500 text-white'
                      : plan.highlighted
                      ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 shadow-lg'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {inCart ? (
                    <span className="flex items-center justify-center space-x-2">
                      <i className="ri-check-line text-lg"></i>
                      <span>In Cart</span>
                    </span>
                  ) : justAdded ? (
                    <span className="flex items-center justify-center space-x-2">
                      <i className="ri-check-line text-lg"></i>
                      <span>Added</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center space-x-2">
                      <i className="ri-shopping-cart-line text-lg"></i>
                      <span>Buy Service</span>
                    </span>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <p className="text-base text-gray-600 mb-4">
            Need individual services? All our services are available from $100 to $750.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;